"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { ArrowRight, MapPin, ShieldCheck, Star } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Hero() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const patientAvatars = [{
    name: "JD",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722013/img/client_one.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722013/img/client_one.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722013/img/client_one.webp-wvc-sizes"
  }, {
    name: "MS",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722013/img/client_two.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722013/img/client_two.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722013/img/client_two.webp-wvc-sizes"
  }, {
    name: "AL",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722013/img/client_three.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722013/img/client_three.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722013/img/client_three.png-wvc-sizes"
  }, {
    name: "RK",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722013/img/client_four.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722013/img/client_four.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722013/img/client_four.png-wvc-sizes"
  }];
  return <section data-section-id="486" id="the-midnight-standard" className="relative w-full overflow-hidden bg-background text-foreground" style={{
    minHeight: "92vh"
  }}>
      <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{
      background: "radial-gradient(60% 50% at 85% 10%, hsl(0 0% 100% / 0.05) 0%, hsl(0 0% 0% / 0) 60%)"
    }} />

      <div className="relative mx-auto grid w-full max-w-[1440px] grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 px-6 sm:px-10 lg:pl-20 lg:pr-12 py-12 lg:py-20 items-center" style={{
      minHeight: "92vh"
    }}>
        {/* Left Column */}
        <div ref={leftRef} className={`order-2 lg:order-1 lg:col-span-5 flex flex-col transition-all duration-700 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {/* Category Tag */}
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-8 bg-foreground/40" aria-hidden="true" />
            <span className="font-mono text-xs uppercase tracking-[0.25em] font-medium text-foreground/70">
              Chauffeured Service
            </span>
          </div>

          {/* Main Headline */}
          

          {/* Location Coverage Badge */}
          <div className="mt-4 inline-flex items-center gap-2 rounded-md border border-border/80 bg-secondary/50 px-3.5 py-2 text-xs font-mono uppercase tracking-wider text-muted-foreground w-fit shadow-xs">
            <MapPin className="size-3.5 text-foreground shrink-0" aria-hidden="true" />
            <span className="font-medium text-foreground/90">
              LOS ANGELES · SAN FRANCISCO · CHICAGO · NEW YORK · MIAMI · ALL MAJOR CITIES
            </span>
          </div>

          {/* Description */}
          <p className="font-default text-muted-foreground mt-6 text-base sm:text-lg leading-relaxed !italic !normal-case" style={{
          maxWidth: "520px"
        }}>
            BEMAX LIMO
            <span className="!uppercase font-semibold block text-foreground not-italic mt-1 text-sm tracking-wider">
              Exceptional Service. Discreet Luxury. Absolute Reliability.
            </span>
            <span className="!italic mt-2 block">
              BEMAX provides a premium private transportation experience designed for discerning clients who value comfort, punctuality, privacy, and professionalism. From airport transfers and private terminal services to corporate transportation and special occasions, every journey is carefully managed with precision and attention to detail.
            </span>
          </p>

          {/* Action CTAs */}
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Button asChild size="lg" className="h-12 px-7 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 shadow-md font-default">
              <Link to="https://book.mylimobiz.com/v4/bemax" newTab>
                Reserve Now
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="h-12 px-7 rounded-lg border-border bg-background text-foreground hover:bg-secondary/60 font-default shadow-xs">
              <Link to="/FleetPage">View Our Fleet</Link>
            </Button>
          </div>

          <Separator className="mt-8 mb-6 bg-border" />

          {/* Trust Metrics */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
            <div className="flex items-center">
              <div className="flex -space-x-3">
                {patientAvatars.map((avatar, i) => <SafeHydrate><Avatar data-index={i} key={i} className="size-10 ring-2 ring-background shadow-sm">
                    <AvatarImage src={avatar.src} data-wvc-srcset={avatar.srcset} data-wvc-sizes={avatar.sizes} alt={`Executive client ${i + 1}`} loading="lazy" />
                    <AvatarFallback className="bg-secondary text-secondary-foreground text-xs font-mono">
                      {avatar.name}
                    </AvatarFallback>
                  </Avatar></SafeHydrate>)}
              </div>
            </div>

            <div className="font-default text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">70,000+</span> journeys completed
            </div>

            <div className="flex items-center gap-1">
              {[0, 1, 2, 3, 4].map(i => <Star data-index={i} key={i} className="h-4 w-4 fill-foreground text-foreground" aria-hidden="true" />)}
            </div>
          </div>
        </div>

        {/* Right Column Image */}
        <div ref={rightRef} className={`order-1 lg:order-2 lg:col-span-7 relative transition-all duration-1000 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="relative w-full overflow-hidden shadow-2xl border border-border" style={{
          borderRadius: "2px",
          height: "clamp(420px, 75vh, 760px)"
        }}>
            <img alt="BEMAX LIMO CHAUFFEURED SERVICE luxury vehicle parked at night" className="absolute inset-0 h-full w-full object-cover" loading="lazy" decoding="async" src="http://bemaxlimo.com/wp-content/uploads/2026/09/IMG_3112.webp" />

            <div aria-hidden="true" className="absolute inset-x-0 top-0 h-32 pointer-events-none" style={{
            background: "linear-gradient(to bottom, hsl(0 0% 0% / 0.6), hsl(0 0% 0% / 0))"
          }} />

            <div className="absolute top-5 right-5 flex items-center gap-2 rounded-lg bg-background/90 backdrop-blur-sm px-3.5 py-2 border border-border shadow-sm" style={{
            borderRadius: "2px"
          }}>
              <ShieldCheck className="h-4 w-4 text-foreground" aria-hidden="true" />
              <span className="font-default text-xs font-medium text-foreground tracking-wide">
                Licensed &amp; Insured
              </span>
            </div>
          </div>

          <div className="absolute -bottom-6 left-4 sm:-bottom-8 sm:-left-6 lg:-left-10 z-10 shadow-xl border border-border bg-card text-card-foreground" style={{
          borderRadius: "2px",
          padding: "18px 22px",
          minWidth: "180px"
        }}>
            <div className="flex items-center gap-2 mb-1">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-lg opacity-75 animate-ping bg-foreground" />
                <span className="relative inline-flex h-2 w-2 rounded-lg bg-foreground" />
              </span>
              <span className="font-mono text-xs uppercase tracking-[0.18em] font-medium text-muted-foreground">
                Available 24/7
              </span>
            </div>
            <div className="font-serif text-2xl font-semibold text-foreground">
              Always Ready
            </div>
          </div>
        </div>
      </div>
    </section>;
}